import { Add } from '@mui/icons-material';
import { Box, Button, Paper, TableContainer, Typography } from '@mui/material';
import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import Chip from '../../components/Chip/Chip';
import DefaultTable from '../../components/DefaultTable/DefaultTable';
import TableToolbar from '../../components/DefaultTable/TableToolbar/TableToolbar';
import FormatCurrency from '../../components/FormatNumber/FormatCurrency';
import GlobalSearch from '../../components/GlobalSearch/GlobalSearch';
import PageTitle from '../../components/PageTitle/PageTitle';
import { InvoiceStatus } from '../../enums/invoiceStatus';
import { useShowFilterOrSearch } from '../../hooks/useShowFilterOrSearch';
import { useTableFunctions } from '../../hooks/useTableFunctions';
import { HeadCell } from '../../models/headCell';
import { Invoice } from '../../models/invoice';
import { Pagination } from '../../models/pagination';
import {
  CREATE_INVOICES,
  DRAFT_INVOICE,
  INVOICES
} from '../../router/constants';
import { dashboardService } from '../../services/dashboardService';
import { invoiceService } from '../../services/invoiceService';
import {
  createDownloadFile,
  removeEmptyObjectFields
} from '../../utils/helpers';
import calculateInvoiceTotal from '../../utils/invoiceTotal';
import { getRoundStatusStyle } from '../../utils/statusRoundStyle';
import CreateInvoiceModal from './CreateInvoiceModal';
import FilterInvoices, { FilterInvoicesForm } from './FilterInvoices';

const initialInvoicesColumnData: HeadCell[] = [
  {
    id: 'id',
    label: 'Invoice #',
    sortable: true,
    visible: true,
    render: (invoice: Invoice) => invoice.invoiceNumber
  },
  {
    id: 'projectId',
    label: 'Project #',
    sortable: true,
    visible: true,
    render: (invoice: Invoice) => invoice?.projectId
  },
  {
    id: 'projectTitle',
    label: 'Project Title',
    sortable: true,
    visible: true,
    render: (invoice: Invoice) => (
      <Box
        width={400}
        whiteSpace="nowrap"
        textOverflow="ellipsis"
        overflow="hidden"
      >
        {invoice?.projectTitle ?? '--'}
      </Box>
    )
  },
  {
    id: 'companyName',
    label: 'Company',
    sortable: true,
    visible: true,
    render: (invoice: Invoice) => invoice?.companyName
  },
  {
    id: 'manager.fullName',
    label: 'Manager',
    sortable: true,
    visible: true,
    render: (invoice: Invoice) => invoice?.manager?.fullName
  },
  {
    id: 'total',
    label: 'Total Invoice',
    sortable: true,
    visible: true,
    render: (invoice: Invoice) => (
      <FormatCurrency value={calculateInvoiceTotal(invoice)} />
    )
  },
  {
    id: 'status',
    label: 'Status',
    sortable: true,
    visible: true,
    thStyle: { textAlign: 'center' },
    render: (invoice: Invoice) => (
      <Box
        sx={{
          ...getRoundStatusStyle(InvoiceStatus[invoice.status]),
          whiteSpace: 'nowrap'
        }}
      >
        {InvoiceStatus[invoice.status]}
      </Box>
    )
  }
];

const InvoicesList = (): JSX.Element => {
  const navigate = useNavigate();
  const location = useLocation();
  const initialFilterData: FilterInvoicesForm = {
    invoiceNumber: '',
    projectId: '',
    projectTitle: '',
    status: location.state ? ['SUBMITTED'] : [],
    total: ''
  };
  const [paginationInvoices, setPaginationInvoices] = useState<
    Pagination<Invoice> | undefined
  >(undefined);
  const [loading, setLoading] = useState<boolean>(true);
  const [showFilters, showSearch, onFilterClick, onSearchClick] =
    useShowFilterOrSearch();
  const {
    columnsData,
    page,
    rowsPerPage,
    sortBy,
    sortType,
    searchTerm,
    filterData,
    onRequestSort,
    onChangePage,
    onChangeRowsPerPage,
    onChangeColumnVisibility,
    onSearchChange,
    onFilterChange
  } = useTableFunctions<FilterInvoicesForm>(
    initialInvoicesColumnData,
    initialFilterData,
    'createdOn',
    'desc'
  );
  const [createInvoiceModal, setCreateInvoiceModal] = useState(false);

  useEffect(() => {
    getInvoices();
  }, [page, rowsPerPage, sortBy, sortType, filterData, searchTerm]);

  const getInvoices = () => {
    invoiceService
      .getInvoices(generateFilterBody(), {
        params: {
          page: page,
          size: rowsPerPage,
          sort: `${sortBy},${sortType}`
        }
      })
      .then((response) => {
        setPaginationInvoices(response.data);
        setLoading(false);
      });
  };

  const handleExport = () => {
    invoiceService.exportInvoices(generateFilterBody()).then((response) => {
      createDownloadFile(response.data, 'invoices');
    });
  };

  const generateFilterBody = () => {
    let body = removeEmptyObjectFields(filterData);
    if (searchTerm) {
      body = {
        ...body,
        searchTerm: searchTerm
      };
    }
    return body;
  };

  const handleCreateInvoice = () => {
    const projectStatus = 'IN_PROGRESS';
    dashboardService
      .getTalentProjects({ params: { status: projectStatus } })
      .then((response) => {
        if (response.data.content.length > 0) {
          setCreateInvoiceModal(true);
        } else {
          navigate(`${CREATE_INVOICES}`);
        }
      });
  };

  return (
    <>
      <Box display="flex" alignItems="center">
        <PageTitle>View All Invoices</PageTitle>
        <Chip size={paginationInvoices?.totalElements} />
        <Box flexGrow={1} />
        <Button
          sx={{ mb: 3 }}
          startIcon={<Add />}
          onClick={() => handleCreateInvoice()}
        >
          Create Invoice
        </Button>
      </Box>
      <TableContainer component={Paper} sx={{ minHeight: '320px' }}>
        <TableToolbar
          viewTableColumnsProps={{
            columns: columnsData,
            onColumnClick: onChangeColumnVisibility
          }}
          onFilter={onFilterClick}
          onSearch={onSearchClick}
          onExport={handleExport}
        >
          {showSearch && (
            <GlobalSearch
              onSearchChange={onSearchChange}
              searchTerm={searchTerm}
            />
          )}
          {showFilters && (
            <FilterInvoices
              handleFormSubmit={onFilterChange}
              filterData={filterData}
            />
          )}
        </TableToolbar>
        <DefaultTable
          columns={columnsData}
          data={paginationInvoices?.content}
          rowClick={(invoice: Invoice) =>
            invoice.status === 'NOT_YET_SUBMITTED'
              ? navigate(`${DRAFT_INVOICE}/${invoice.id}`)
              : navigate(`${INVOICES}/${invoice.id}`)
          }
          sortProps={{ sortBy, sortType, onRequestSort }}
          paginationProps={{
            page,
            rowsPerPage,
            onChangePage,
            onChangeRowsPerPage,
            totalElements: paginationInvoices?.totalElements
          }}
        />
        {!loading && !paginationInvoices?.content.length && (
          <Typography variant="h5" align="center" m={4}>
            No Invoices found!
          </Typography>
        )}
      </TableContainer>
      <CreateInvoiceModal
        open={createInvoiceModal}
        closeModal={() => setCreateInvoiceModal(false)}
      />
    </>
  );
};

export default InvoicesList;
